import {
  X,
  AlertTriangle,
  CheckCircle2,
  Clock,
} from 'lucide-react'
import { clsx, type ClassValue } from 'clsx'
import { twMerge } from 'tailwind-merge'

function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

interface IntelligenceRailProps {
  open: boolean
  onClose: () => void
}

const INTELLIGENCE_ITEMS = [
  { id: 'ruling-004', label: 'Ruling 004 Pending', type: 'warning' as const, surface: 'Portfolio', time: '2h ago' },
  { id: 'ruling-005', label: 'Ruling 005 Pending', type: 'warning' as const, surface: 'Portfolio', time: '2h ago' },
  { id: 'ruling-006', label: 'Ruling 006 Pending', type: 'warning' as const, surface: 'Registry', time: '2h ago' },
  { id: 'ruling-007', label: 'Ruling 007 Pending', type: 'warning' as const, surface: 'Registry', time: '2h ago' },
  { id: 'ruling-008', label: 'Ruling 008 Pending', type: 'warning' as const, surface: 'Writing Stone', time: '2h ago' },
  { id: 'ws-004', label: 'WS_004 Resolved', type: 'success' as const, surface: 'Writing Stone', time: '4h ago' },
  { id: 'evidence-audit', label: 'Evidence Audit Complete', type: 'info' as const, surface: 'Evidence', time: '6h ago' },
  { id: 'portfolio-pruned', label: '433 Public Count Locked', type: 'success' as const, surface: 'Portfolio', time: '8h ago' },
]

const TYPE_ICONS = {
  warning: AlertTriangle,
  success: CheckCircle2,
  info: Clock,
  danger: AlertTriangle,
}

const TYPE_COLORS = {
  warning: 'text-ark-warning bg-ark-warning/10',
  success: 'text-ark-success bg-ark-success/10',
  info: 'text-ark-info bg-ark-info/10',
  danger: 'text-ark-danger bg-ark-danger/10',
}

export function IntelligenceRail({ open, onClose }: IntelligenceRailProps) {
  if (!open) return null

  return (
    <aside className="fixed right-0 top-12 bottom-[2.25rem] w-72 bg-ark-deep/95 backdrop-blur-md border-l border-ark-panel/50 z-ark-shell flex flex-col transition-all duration-500 ease-ark">
      {/* Rail Header */}
      <div className="h-10 flex items-center justify-between px-4 border-b border-ark-panel/30">
        <span className="text-xs font-mono font-medium text-ark-text-dim uppercase tracking-wider">
          Intelligence Feed
        </span>
        <button
          onClick={onClose}
          className="p-1 rounded-ark-sm hover:bg-ark-panel/50 text-ark-text-dim hover:text-ark-text transition-colors"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>

      {/* Stats Summary */}
      <div className="p-3 grid grid-cols-3 gap-2 border-b border-ark-panel/30">
        <div className="bg-ark-panel/30 rounded-ark-sm p-2 text-center">
          <div className="text-lg font-bold text-ark-warning">5</div>
          <div className="text-[0.6rem] text-ark-text-muted uppercase tracking-wider">Pending</div>
        </div>
        <div className="bg-ark-panel/30 rounded-ark-sm p-2 text-center">
          <div className="text-lg font-bold text-ark-success">3</div>
          <div className="text-[0.6rem] text-ark-text-muted uppercase tracking-wider">Resolved</div>
        </div>
        <div className="bg-ark-panel/30 rounded-ark-sm p-2 text-center">
          <div className="text-lg font-bold text-ark-info">27</div>
          <div className="text-[0.6rem] text-ark-text-muted uppercase tracking-wider">Evidence</div>
        </div>
      </div>

      {/* Feed */}
      <div className="flex-1 overflow-auto p-3 space-y-2">
        <div className="text-[0.6rem] font-mono text-ark-text-muted uppercase tracking-wider mb-2">
          Recent Activity
        </div>
        {INTELLIGENCE_ITEMS.map((item) => {
          const Icon = TYPE_ICONS[item.type]
          return (
            <div
              key={item.id}
              className="flex items-start gap-2.5 p-2.5 rounded-ark-sm bg-ark-panel/20 hover:bg-ark-panel/40 transition-colors cursor-pointer group"
            >
              <div className={cn('p-1 rounded-ark-sm', TYPE_COLORS[item.type])}>
                <Icon className="w-3 h-3" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-xs text-ark-text-bright group-hover:text-ark-text transition-colors">
                  {item.label}
                </div>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-[0.65rem] text-ark-text-muted">{item.surface}</span>
                  <span className="text-[0.65rem] text-ark-text-muted">·</span>
                  <span className="text-[0.65rem] text-ark-text-muted">{item.time}</span>
                </div>
              </div>
            </div>
          )
        })}
      </div>

      {/* Rail Footer */}
      <div className="p-3 border-t border-ark-panel/30">
        <div className="flex items-center justify-between text-[0.65rem] text-ark-text-muted">
          <span>5 items require attention</span>
          <span className="text-ark-warning">Action needed</span>
        </div>
      </div>
    </aside>
  )
}
